import React, { useState, useEffect, useMemo } from "react";
import { FaSearch, FaWallet, FaFilter } from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import EmptyState from "../Components/States/EmptyState";
import OfflineState from "../Components/States/OfflineState";
import LoadingState from "../Components/States/LoadingState";
import { isNetworkError } from "../utils/network";
import { getWallet } from "../api/staff.api";
import { getStaffId } from "../utils/storage";
import {
  formatWalletAmount,
  getTotalWalletBalance,
  parseWalletAmount,
} from "../utils/wallet";

const FROM_CATEGORY = {
  Admin: "Wallet",
  "Admin (All Staff)": "Wallet",
  "Diagnostics Booking": "Lab Tests",
  "Package Booking": "Packages",
  "Package Booking (Admin)": "Packages",
  "Doctor Consultation": "Consultations",
  "Doctor Consultation (Admin)": "Consultations",
  "Doctor Appointment": "Consultations",
  "Booking Cancellation": "Refunds",
  "Coins Redemption": "Consultations",
  "Bulk Package Booking": "Packages",
};

const categorize = (fromText) => FROM_CATEGORY[fromText] || "Other";

const CATEGORY_COLORS = {
  Consultations: "bg-primary",
  "Lab Tests": "bg-red-700",
  Refunds: "bg-amber-600",
  Wallet: "bg-gray-600",
  Packages: "bg-teal-600",
  Other: "bg-gray-400",
};

const formatFromText = (fromText) => {
  if (fromText == null || String(fromText).trim() === "") return "Transaction";
  return String(fromText);
};

const PAGE_SIZE = 10;

const WalletPage = () => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);

  const staffId = getStaffId();

  const loadWallet = () => {
    if (!staffId) {
      setError("Staff ID not found!");
      setLoading(false);
      return;
    }
    setLoading(true);
    setNetworkError(false);
    setError(null);
    getWallet(staffId)
      .then((res) => setWalletData(res.data || null))
      .catch((err) => {
        if (isNetworkError(err)) setNetworkError(true);
        else setError("Error fetching wallet data");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadWallet();
  }, [staffId]);

  const balance = getTotalWalletBalance(walletData);
  const forTests = parseWalletAmount(walletData?.forTests);
  const forDoctors = parseWalletAmount(walletData?.forDoctors);
  const forPackages = parseWalletAmount(walletData?.forPackages);
  const transactions = useMemo(() => walletData?.transaction_history || [], [walletData]);
  const spent = transactions
    .filter((t) => t.type === "debit")
    .reduce((sum, t) => sum + parseWalletAmount(t.totalAmount), 0);
  const totalBudget = spent + balance;
  const usedPct = totalBudget > 0 ? Math.min(100, Math.round((spent / totalBudget) * 100)) : 0;

  const categoryTotals = useMemo(() => {
    const map = {};
    transactions
      .filter((t) => t.type === "debit")
      .forEach((t) => {
        const cat = categorize(t.from);
        map[cat] = (map[cat] || 0) + parseWalletAmount(t.totalAmount);
      });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [transactions]);

  const filteredTx = useMemo(() => {
    let list = transactions;
    if (categoryFilter) {
      list = list.filter((t) => categorize(t.from) === categoryFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          formatFromText(t.from).toLowerCase().includes(q) ||
          (t.provider || "").toLowerCase().includes(q) ||
          (t.time_ago || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, search, categoryFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTx.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedTx = filteredTx.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const searchInput = (
    <div className="relative w-full">
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" size={14} />
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search transactions…"
        className="w-full pl-9 pr-4 py-2.5 border border-surface-container rounded-full text-sm bg-surface-lowest"
      />
    </div>
  );

  const filterButton = (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setShowFilter((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-primary font-medium px-2 py-1.5 rounded-lg hover:bg-primary-fixed/10"
      >
        <FaFilter size={12} />
        Filter{categoryFilter ? `: ${categoryFilter}` : ""}
      </button>
      {showFilter && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            aria-label="Close filter"
            onClick={() => setShowFilter(false)}
          />
          <div className="absolute right-0 lg:right-0 top-full mt-1 left-auto w-48 max-w-[calc(100vw-2rem)] bg-surface-lowest border border-surface-container rounded-lg shadow-lg z-20 py-1">
            <button type="button" onClick={() => { setCategoryFilter(""); setShowFilter(false); }} className="block w-full text-left px-4 py-2.5 text-sm hover:bg-surface-low">All</button>
            {Object.keys(CATEGORY_COLORS).map((c) => (
              <button key={c} type="button" onClick={() => { setCategoryFilter(c); setShowFilter(false); }} className="block w-full text-left px-4 py-2.5 text-sm hover:bg-surface-low">{c}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderTxCard = (t, i) => (
    <div key={i} className="px-4 py-4 border-b border-surface-container last:border-0">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`w-2 h-2 rounded-full shrink-0 ${CATEGORY_COLORS[categorize(t.from)] || "bg-gray-400"}`} />
          <span className="font-medium text-on-primary-fixed-var text-sm break-words">{formatFromText(t.from)}</span>
        </div>
        <span className={`shrink-0 text-sm font-semibold ${t.type === "debit" ? "text-red-600" : "text-green-600"}`}>
          {t.type === "debit" ? "-" : "+"}₹{formatWalletAmount(t.totalAmount)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 pl-4">
        <div className="min-w-0">
          <p className="text-xs text-on-surface-variant">{t.time_ago || "—"}</p>
          {t.provider && <p className="text-xs text-on-surface-variant mt-0.5 truncate">{t.provider}</p>}
        </div>
        <span className="shrink-0 inline-block px-2 py-0.5 text-xs rounded bg-surface-container text-on-surface-variant">
          Processed
        </span>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingState fullPage withHeader message="Loading wallet…" />;
  }

  if (networkError) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <OfflineState onRetry={loadWallet} className="flex-1" />
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-red-500">{error}</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex-1 pb-16 lg:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-on-primary-fixed-var">Corporate Wallet Overview</h1>
            <p className="text-sm text-on-surface-variant mt-1 hidden lg:block">
              Manage your annual wellness budget and track utilization.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Balance card */}
            <div className="bg-gradient-to-br from-primary-fixed/30 to-primary-fixed/10 border border-surface-container rounded-2xl p-4 sm:p-6 relative overflow-hidden">
              <div className="flex justify-between items-start mb-4">
                <p className="text-xs font-semibold tracking-wide text-on-surface-variant uppercase">Available Balance</p>
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                  <FaWallet className="text-white" size={18} />
                </div>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-primary mb-4 sm:mb-6">₹{formatWalletAmount(balance)}</p>
              {balance === 0 && transactions.length === 0 && (
                <p className="text-sm text-on-surface-variant mb-4">Your wallet is empty.</p>
              )}
              {totalBudget > 0 && (
                <>
                  <div className="flex justify-between text-xs text-on-surface-variant mb-2">
                    <span>₹{spent.toLocaleString("en-IN")} Spent</span>
                    <span>Total Budget: ₹{totalBudget.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="h-2 bg-surface-container rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${usedPct}%` }} />
                  </div>
                  <p className="text-xs text-on-surface-variant">
                    {usedPct}% of your annual budget utilized.
                  </p>
                </>
              )}
            </div>

            {/* Category breakdown */}
            <div className="bg-surface-lowest border border-surface-container rounded-2xl p-4 sm:p-6">
              <h2 className="font-semibold text-on-primary-fixed-var mb-4">Balance by category</h2>
              <ul className="space-y-3 mb-4">
                {[
                  ["Lab tests", forTests],
                  ["Doctors", forDoctors],
                  ["Packages", forPackages],
                ].map(([label, amount]) => (
                  <li key={label} className="flex items-center justify-between text-sm">
                    <span className="text-on-primary-fixed-var">{label}</span>
                    <span className="font-medium">₹{formatWalletAmount(amount)}</span>
                  </li>
                ))}
              </ul>
              <h3 className="font-semibold text-on-primary-fixed-var mb-3 text-sm">Spending</h3>
              {categoryTotals.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No spending data yet.</p>
              ) : (
                <ul className="space-y-3">
                  {categoryTotals.map(([cat, amount]) => (
                    <li key={cat} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-on-primary-fixed-var">
                        <span className={`w-2.5 h-2.5 rounded-full ${CATEGORY_COLORS[cat] || "bg-gray-400"}`} />
                        {cat}
                      </span>
                      <span className="font-medium">₹{amount.toLocaleString("en-IN")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Transactions */}
          <div className="bg-surface-lowest border border-surface-container rounded-2xl overflow-hidden">
            {/* Desktop toolbar: title | search | filter */}
            <div className="hidden lg:flex items-center gap-4 px-5 py-4 border-b border-surface-container">
              <h2 className="font-semibold text-on-primary-fixed-var shrink-0">Recent Transactions</h2>
              <div className="flex-1 max-w-sm mx-2">{searchInput}</div>
              {filterButton}
            </div>

            {/* Mobile toolbar: title + filter, then search */}
            <div className="lg:hidden px-4 py-3 border-b border-surface-container">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="font-semibold text-on-primary-fixed-var">Recent Transactions</h2>
                {filterButton}
              </div>
              {searchInput}
            </div>

            {/* Mobile cards */}
            <div className="md:hidden">
              {filteredTx.length === 0 ? (
                <div className="px-4 py-4">
                  <EmptyState
                    title="No transactions found."
                    description="Your wallet activity will appear here once you use your corporate wellness benefits."
                    className="border-0 bg-transparent py-6"
                  />
                </div>
              ) : (
                pagedTx.map(renderTxCard)
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-on-surface-variant border-b border-surface-container">
                    <th className="px-5 py-3 font-medium">Date</th>
                    <th className="px-5 py-3 font-medium">Service Description</th>
                    <th className="px-5 py-3 font-medium hidden md:table-cell">Provider</th>
                    <th className="px-5 py-3 font-medium text-right">Amount</th>
                    <th className="px-5 py-3 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTx.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-4">
                        <EmptyState
                          title="No transactions found."
                          description="Your wallet activity will appear here once you use your corporate wellness benefits."
                          className="border-0 bg-transparent py-6"
                        />
                      </td>
                    </tr>
                  ) : (
                    pagedTx.map((t, i) => (
                      <tr key={i} className="border-b border-surface-container last:border-0 hover:bg-surface-low/50">
                        <td className="px-5 py-4 text-on-surface-variant whitespace-nowrap">{t.time_ago || "—"}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${CATEGORY_COLORS[categorize(t.from)] || "bg-gray-400"}`} />
                            <span className="font-medium text-on-primary-fixed-var">{formatFromText(t.from)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-on-surface-variant hidden md:table-cell">{t.provider || "—"}</td>
                        <td className={`px-5 py-4 text-right font-semibold ${t.type === "debit" ? "text-red-600" : "text-green-600"}`}>
                          {t.type === "debit" ? "-" : "+"}₹{formatWalletAmount(t.totalAmount)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-surface-container text-on-surface-variant">
                            Processed
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredTx.length > PAGE_SIZE && (
              <div className="px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-surface-container">
                <p className="text-sm text-on-surface-variant">
                  Page {safePage} of {totalPages} · {filteredTx.length} transactions
                </p>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg border border-surface-container disabled:opacity-40 hover:bg-surface-low"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg border border-surface-container disabled:opacity-40 hover:bg-surface-low"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default WalletPage;
